import { type FragmentOf } from "gql.tada";
import { ZodError, z } from "zod";

import gqlClient from "../../dependencies/gqlClient";
import { Logger } from "../../dependencies/logger";
import type { ArrayElement } from "../../types";
import AvailabilitySearchFragment from "../fragments/availabilitySearch";
import AllAvailabilitySearchQuery, {
  type QueryVariables,
} from "../queries/allAvailabilitySearch";

const logger = new Logger("RESTAURANT_STORE - ALL_AVAILABILITY_SEARCH", "LOG");
// logger.enable();

const sectionOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number(),
  sectionState: z.boolean(),
  isSectionBlocked: z.boolean(),
  availableTableIds: z.array(z.any()).nullable(),
});

const areaOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const promotionOptionSchema = z.object({
  id: z.number(),
  name: z.string(),
  leaveTime: z.string(),
  paymentRequired: z.boolean(),
  preSelected: z.boolean(),
  price: z.number(),
  title: z.string(),
});

const categoryOption = z.union([
  sectionOptionSchema,
  areaOptionSchema,
  promotionOptionSchema,
]);

export type CategoryOption = z.infer<typeof categoryOption>;

const categoryOptionsSchema = z
  .union([z.array(categoryOption), z.record(z.string(), categoryOption)])
  .optional()
  .nullable();

export type CategoryOptions = z.infer<typeof categoryOptionsSchema>;

const nonEmptyAvailabilityOptionsSchema = z.record(
  z.string(),
  categoryOptionsSchema,
);

export type NonEmptyAvailabilityOptions = z.infer<
  typeof nonEmptyAvailabilityOptionsSchema
>;

const availabilityOptionsSchema = z
  .union([nonEmptyAvailabilityOptionsSchema, z.array(z.any())])
  .optional()
  .nullable();

export type AvailabilitySlotOptions = z.infer<typeof availabilityOptionsSchema>;

export type AvailabilitySearchType = FragmentOf<
  typeof AvailabilitySearchFragment
>;

type AvailableTimes = NonNullable<
  ArrayElement<NonNullable<AvailabilitySearchType["availableTimes"]>>
>;

export type AvailabilitySearchSlotWithOptions = Omit<
  AvailableTimes,
  "options"
> & {
  options: AvailabilitySlotOptions;
};

export type AvailabilitySearchWithOptions = AvailabilitySearchType & {
  availableTimes: AvailabilitySearchSlotWithOptions[];
};

export type GetAllAvailabilitySearchParams = QueryVariables;

export default async function getAllAvailabilitySearch(
  variables: GetAllAvailabilitySearchParams,
  abortSignal?: AbortSignal,
): Promise<AvailabilitySearchWithOptions[]> {
  const result = await gqlClient({
    query: AllAvailabilitySearchQuery,
    variables,
    abortSignal,
  });

  const cleanAvailability = (result.allAvailabilitySearch || [])
    .filter((availability) => availability !== null)
    .map((availability) => ({
      ...availability,
      availableTimes: (availability.availableTimes || [])
        .filter((slot) => slot !== null)
        .map((slot) => {
          try {
            return {
              ...slot,
              options: availabilityOptionsSchema.parse(slot.options),
            };
          } catch (error) {
            logger.warn(
              "Failed to parse search availability slot options",
              slot.options,
            );
            if (error instanceof ZodError) {
              logger.warn("Zod issues", error.issues);
            }
          }

          return { ...slot, options: undefined };
        }),
    }));

  return cleanAvailability;
}
